import React, { useState, useEffect, useMemo } from "react";
import { Users, Save, CheckCircle, Target, Star, Search, Loader2 } from "lucide-react";
import axios from "axios";
import { NODE_BACKEND_URL } from "../utils/constant";
import { errorMessage, getAllSports, getAuthToken, getSetting, getUser, getUserSettings, notifyTeamChange } from "../utils/helpers";
import { goToLogin } from "../utils/navigation";
import { Button, Tabs, TabsList, TabsTrigger, TabsContent, Badge, cn } from "./design-system";
import { SETTINGS_LABELS } from "../constants/userFacingLabels";
import { getTeamsBySportCode } from "../lib/teamConfig/teams";
import { useTeam } from "../lib/teamConfig";
import wbb_data from "../data/wbb_nil.json";
import sportsConfig from "../data/sports.json";
import { NBA_TEAMS, WNBA_TEAMS, EUROLEAGUE_TEAMS } from "../utils/basketballDataService";
import { fetchNatStatTeams, isNatStatBasketball, getAllFarmTeams } from "../utils/natstatDataService";
import NavigationSettings from "./settings/NavigationSettings";

const Settings = ({ setShowLogo }) => {
  // Get switchTeam from TeamProvider to update platform sport/theming based on default team
  const { switchTeam } = useTeam();

  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedSports, setSelectedSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState({});
  const [defaultSport, setDefaultSport] = useState(null);
  const [sports, setSports] = useState([]);
  const [loadingSports, setLoadingSports] = useState(false);
  const [sportError, setSportError] = useState(null);
  const [sportSearch, setSportSearch] = useState("");
  const [teamSearch, setTeamSearch] = useState("");
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [activeTab, setActiveTab] = useState("sport");
  const [isSaving, setIsSaving] = useState(false);
  const [performanceCriteria, setPerformanceCriteria] = useState("");

  const user = getUser();
  const setting = getSetting();
  const all_sports = setting?.sports || [];
  const sports_detail = getAllSports()?.filter((v) => all_sports.includes(v.code));

  const fetchUserSports = async () => {
    setLoadingSports(true);
    setSportError(null);
    try {
      const userStr = sessionStorage.getItem("user");
      if (!userStr) { setSportError("User not found."); setLoadingSports(false); return; }
      const userData = JSON.parse(userStr);
      if (!userData?.id) { setSportError("Invalid user data."); setLoadingSports(false); return; }

      const response = await axios.get(`${NODE_BACKEND_URL}/api/user/${userData.id}/sports`, {
        headers: { Authorization: getAuthToken() }
      });

      if (response.data?.success && Array.isArray(response.data.sports)) {
        const backendSports = response.data.sports;
        const backendCodes = new Set(backendSports.map(s => s.code));
        const enabledConfigSports = sportsConfig
          .filter(s => s.isEnable && !backendCodes.has(s.code))
          .map(s => ({ id: s.code.toLowerCase(), name: s.fullName || s.code, code: s.code, isEnable: true }));
        setSports([...backendSports, ...enabledConfigSports]);
      } else {
        setSports(sportsConfig.filter(s => s.isEnable).map(s => ({
          id: s.code.toLowerCase(), name: s.fullName || s.code, code: s.code, isEnable: true
        })));
      }
    } catch (error) {
      if (!error?.response && error?.code === "ERR_NETWORK") goToLogin();
      setSports(sportsConfig.filter(s => s.isEnable).map(s => ({
        id: s.code.toLowerCase(), name: s.fullName || s.code, code: s.code, isEnable: true
      })));
    } finally {
      setLoadingSports(false);
    }
  };

  useEffect(() => {
    fetchUserSports();
    if (user?.id) getUserSettings(user?.id);

    const savedSport = sessionStorage.getItem("selectedSport");
    if (savedSport) {
      const parsed = JSON.parse(savedSport);
      setSelectedSport(parsed);
      setDefaultSport(parsed);
    }

    const savedSports = sessionStorage.getItem("selectedSports");
    if (savedSports) {
      const parsed = JSON.parse(savedSports);
      if (Array.isArray(parsed) && parsed.every(item => typeof item === 'object')) {
        setSelectedSports(parsed);
      } else {
        setSelectedSports(sports_detail);
      }
    } else {
      setSelectedSports(sports_detail);
    }

    if (setting?.performance_criteria) setPerformanceCriteria(setting?.performance_criteria);
  }, []);

  useEffect(() => {
    const savedSport = sessionStorage.getItem("selectedSport");
    if (savedSport) {
      const parsed = JSON.parse(savedSport);
      if (parsed?.code) fetchTeams(parsed);
    }
  }, []);

  const fetchTeams = async (sport) => {
    setLoadingTeams(true);
    setTeams([]);

    try {
      let enhancedTeams = [];

      // Check if this is a NatStat basketball league - try BigQuery first
      if (isNatStatBasketball(sport.code)) {
        const natstatTeams = await fetchNatStatTeams(sport.code);
        if (natstatTeams.length > 0) {
          enhancedTeams = natstatTeams;
        }
      }

      // Farm teams (Minor League Baseball affiliates)
      if (sport.code === "MiLB") {
        enhancedTeams = getAllFarmTeams();
      }

      // Fallback to hardcoded data if NatStat API didn't return data
      if (enhancedTeams.length === 0 && sport.code === "MBB") {
        const response = await axios.get(`https://slam-all-python-359065791766.us-central1.run.app/MBB/team-stats-mia/?schema=MBB`, {
          headers: { Authorization: getAuthToken() }
        });
        if (response?.data?.data) {
          enhancedTeams = response.data.data.map(team => ({
            team_id: String(team.rank || team.team_id), team_name: team.team || "Unknown",
            full_name: team.team || "", conference: team.Conference || ""
          }));
        }
      } else if (enhancedTeams.length === 0 && sport.code === "WBB") {
        const teamSet = new Map();
        wbb_data.forEach(player => {
          if (player.last_team && !teamSet.has(player.last_team)) {
            teamSet.set(player.last_team, {
              team_id: player.last_team.toLowerCase().replace(/\s+/g, '-'),
              team_name: player.last_team, full_name: player.last_team, conference: ""
            });
          }
        });
        enhancedTeams = Array.from(teamSet.values());
      } else if (sport.code === "MLB") {
        const mlbTeams = [
          { name: "Arizona Diamondbacks", abbrev: "ARI", conf: "NL West" },
          { name: "Atlanta Braves", abbrev: "ATL", conf: "NL East" },
          { name: "Baltimore Orioles", abbrev: "BAL", conf: "AL East" },
          { name: "Boston Red Sox", abbrev: "BOS", conf: "AL East" },
          { name: "Chicago Cubs", abbrev: "CHC", conf: "NL Central" },
          { name: "Chicago White Sox", abbrev: "CWS", conf: "AL Central" },
          { name: "Cincinnati Reds", abbrev: "CIN", conf: "NL Central" },
          { name: "Cleveland Guardians", abbrev: "CLE", conf: "AL Central" },
          { name: "Colorado Rockies", abbrev: "COL", conf: "NL West" },
          { name: "Detroit Tigers", abbrev: "DET", conf: "AL Central" },
          { name: "Houston Astros", abbrev: "HOU", conf: "AL West" },
          { name: "Kansas City Royals", abbrev: "KC", conf: "AL Central" },
          { name: "Los Angeles Angels", abbrev: "LAA", conf: "AL West" },
          { name: "Los Angeles Dodgers", abbrev: "LAD", conf: "NL West" },
          { name: "Miami Marlins", abbrev: "MIA", conf: "NL East" },
          { name: "Milwaukee Brewers", abbrev: "MIL", conf: "NL Central" },
          { name: "Minnesota Twins", abbrev: "MIN", conf: "AL Central" },
          { name: "New York Mets", abbrev: "NYM", conf: "NL East" },
          { name: "New York Yankees", abbrev: "NYY", conf: "AL East" },
          { name: "Oakland Athletics", abbrev: "OAK", conf: "AL West" },
          { name: "Philadelphia Phillies", abbrev: "PHI", conf: "NL East" },
          { name: "Pittsburgh Pirates", abbrev: "PIT", conf: "NL Central" },
          { name: "San Diego Padres", abbrev: "SD", conf: "NL West" },
          { name: "San Francisco Giants", abbrev: "SF", conf: "NL West" },
          { name: "Seattle Mariners", abbrev: "SEA", conf: "AL West" },
          { name: "St. Louis Cardinals", abbrev: "STL", conf: "NL Central" },
          { name: "Tampa Bay Rays", abbrev: "TB", conf: "AL East" },
          { name: "Texas Rangers", abbrev: "TEX", conf: "AL West" },
          { name: "Toronto Blue Jays", abbrev: "TOR", conf: "AL East" },
          { name: "Washington Nationals", abbrev: "WSH", conf: "NL East" },
        ];
        enhancedTeams = mlbTeams.map(t => ({ team_id: t.abbrev.toLowerCase(), team_name: t.name, full_name: t.name, conference: t.conf }));
      } else if (sport.code === "CBB") {
        // Static D1 College Baseball teams - no API call needed (fast loading)
        const cbbTeams = [
          // ACC
          { name: "Boston College", abbrev: "BC", conf: "ACC" },
          { name: "Clemson", abbrev: "CLEM", conf: "ACC" },
          { name: "Duke", abbrev: "DUKE", conf: "ACC" },
          { name: "Florida State", abbrev: "FSU", conf: "ACC" },
          { name: "Georgia Tech", abbrev: "GT", conf: "ACC" },
          { name: "Louisville", abbrev: "LOU", conf: "ACC" },
          { name: "Miami", abbrev: "MIA", conf: "ACC" },
          { name: "North Carolina", abbrev: "UNC", conf: "ACC" },
          { name: "NC State", abbrev: "NCST", conf: "ACC" },
          { name: "Notre Dame", abbrev: "ND", conf: "ACC" },
          { name: "Pittsburgh", abbrev: "PITT", conf: "ACC" },
          { name: "Virginia", abbrev: "UVA", conf: "ACC" },
          { name: "Virginia Tech", abbrev: "VT", conf: "ACC" },
          { name: "Wake Forest", abbrev: "WAKE", conf: "ACC" },
          // SEC
          { name: "Alabama", abbrev: "ALA", conf: "SEC" },
          { name: "Arkansas", abbrev: "ARK", conf: "SEC" },
          { name: "Auburn", abbrev: "AUB", conf: "SEC" },
          { name: "Florida", abbrev: "FLA", conf: "SEC" },
          { name: "Georgia", abbrev: "UGA", conf: "SEC" },
          { name: "Kentucky", abbrev: "UK", conf: "SEC" },
          { name: "LSU", abbrev: "LSU", conf: "SEC" },
          { name: "Mississippi State", abbrev: "MSST", conf: "SEC" },
          { name: "Missouri", abbrev: "MIZ", conf: "SEC" },
          { name: "Oklahoma", abbrev: "OU", conf: "SEC" },
          { name: "Ole Miss", abbrev: "MISS", conf: "SEC" },
          { name: "South Carolina", abbrev: "SC", conf: "SEC" },
          { name: "Tennessee", abbrev: "TENN", conf: "SEC" },
          { name: "Texas", abbrev: "TEX", conf: "SEC" },
          { name: "Texas A&M", abbrev: "TAMU", conf: "SEC" },
          { name: "Vanderbilt", abbrev: "VAN", conf: "SEC" },
          // Big 12
          { name: "Arizona", abbrev: "ARIZ", conf: "Big 12" },
          { name: "Arizona State", abbrev: "ASU", conf: "Big 12" },
          { name: "Baylor", abbrev: "BAY", conf: "Big 12" },
          { name: "BYU", abbrev: "BYU", conf: "Big 12" },
          { name: "Cincinnati", abbrev: "CIN", conf: "Big 12" },
          { name: "Colorado", abbrev: "COL", conf: "Big 12" },
          { name: "Houston", abbrev: "HOU", conf: "Big 12" },
          { name: "Iowa State", abbrev: "ISU", conf: "Big 12" },
          { name: "Kansas", abbrev: "KU", conf: "Big 12" },
          { name: "Kansas State", abbrev: "KSU", conf: "Big 12" },
          { name: "Oklahoma State", abbrev: "OKST", conf: "Big 12" },
          { name: "TCU", abbrev: "TCU", conf: "Big 12" },
          { name: "Texas Tech", abbrev: "TTU", conf: "Big 12" },
          { name: "UCF", abbrev: "UCF", conf: "Big 12" },
          { name: "Utah", abbrev: "UTAH", conf: "Big 12" },
          { name: "West Virginia", abbrev: "WVU", conf: "Big 12" },
          // Big Ten
          { name: "Illinois", abbrev: "ILL", conf: "Big Ten" },
          { name: "Indiana", abbrev: "IND", conf: "Big Ten" },
          { name: "Iowa", abbrev: "IOWA", conf: "Big Ten" },
          { name: "Maryland", abbrev: "MD", conf: "Big Ten" },
          { name: "Michigan", abbrev: "MICH", conf: "Big Ten" },
          { name: "Michigan State", abbrev: "MSU", conf: "Big Ten" },
          { name: "Minnesota", abbrev: "MINN", conf: "Big Ten" },
          { name: "Nebraska", abbrev: "NEB", conf: "Big Ten" },
          { name: "Northwestern", abbrev: "NW", conf: "Big Ten" },
          { name: "Ohio State", abbrev: "OSU", conf: "Big Ten" },
          { name: "Oregon", abbrev: "ORE", conf: "Big Ten" },
          { name: "Oregon State", abbrev: "ORST", conf: "Big Ten" },
          { name: "Penn State", abbrev: "PSU", conf: "Big Ten" },
          { name: "Purdue", abbrev: "PUR", conf: "Big Ten" },
          { name: "Rutgers", abbrev: "RUT", conf: "Big Ten" },
          { name: "UCLA", abbrev: "UCLA", conf: "Big Ten" },
          { name: "USC", abbrev: "USC", conf: "Big Ten" },
          { name: "Washington", abbrev: "UW", conf: "Big Ten" },
          { name: "Wisconsin", abbrev: "WIS", conf: "Big Ten" },
          // Pac-12
          { name: "Cal", abbrev: "CAL", conf: "Pac-12" },
          { name: "Stanford", abbrev: "STAN", conf: "Pac-12" },
          // AAC
          { name: "Charlotte", abbrev: "CHAR", conf: "AAC" },
          { name: "East Carolina", abbrev: "ECU", conf: "AAC" },
          { name: "FAU", abbrev: "FAU", conf: "AAC" },
          { name: "Memphis", abbrev: "MEM", conf: "AAC" },
          { name: "Navy", abbrev: "NAVY", conf: "AAC" },
          { name: "Rice", abbrev: "RICE", conf: "AAC" },
          { name: "South Florida", abbrev: "USF", conf: "AAC" },
          { name: "Temple", abbrev: "TEM", conf: "AAC" },
          { name: "Tulane", abbrev: "TUL", conf: "AAC" },
          { name: "UAB", abbrev: "UAB", conf: "AAC" },
          { name: "UTSA", abbrev: "UTSA", conf: "AAC" },
          { name: "Wichita State", abbrev: "WICH", conf: "AAC" },
          // Sun Belt
          { name: "Appalachian State", abbrev: "APP", conf: "Sun Belt" },
          { name: "Arkansas State", abbrev: "ARST", conf: "Sun Belt" },
          { name: "Coastal Carolina", abbrev: "CCU", conf: "Sun Belt" },
          { name: "Georgia Southern", abbrev: "GASO", conf: "Sun Belt" },
          { name: "Georgia State", abbrev: "GAST", conf: "Sun Belt" },
          { name: "James Madison", abbrev: "JMU", conf: "Sun Belt" },
          { name: "Louisiana", abbrev: "ULL", conf: "Sun Belt" },
          { name: "Louisiana-Monroe", abbrev: "ULM", conf: "Sun Belt" },
          { name: "Marshall", abbrev: "MAR", conf: "Sun Belt" },
          { name: "Old Dominion", abbrev: "ODU", conf: "Sun Belt" },
          { name: "South Alabama", abbrev: "USA", conf: "Sun Belt" },
          { name: "Southern Miss", abbrev: "USM", conf: "Sun Belt" },
          { name: "Texas State", abbrev: "TXST", conf: "Sun Belt" },
          { name: "Troy", abbrev: "TROY", conf: "Sun Belt" },
          // Conference USA
          { name: "FIU", abbrev: "FIU", conf: "C-USA" },
          { name: "Jacksonville State", abbrev: "JAX", conf: "C-USA" },
          { name: "Kennesaw State", abbrev: "KSU", conf: "C-USA" },
          { name: "Liberty", abbrev: "LIB", conf: "C-USA" },
          { name: "Louisiana Tech", abbrev: "LT", conf: "C-USA" },
          { name: "Middle Tennessee", abbrev: "MTSU", conf: "C-USA" },
          { name: "New Mexico State", abbrev: "NMSU", conf: "C-USA" },
          { name: "Sam Houston", abbrev: "SHSU", conf: "C-USA" },
          { name: "Western Kentucky", abbrev: "WKU", conf: "C-USA" },
          // Mountain West
          { name: "Air Force", abbrev: "AF", conf: "MW" },
          { name: "Fresno State", abbrev: "FRES", conf: "MW" },
          { name: "Nevada", abbrev: "NEV", conf: "MW" },
          { name: "New Mexico", abbrev: "UNM", conf: "MW" },
          { name: "San Diego State", abbrev: "SDSU", conf: "MW" },
          { name: "San Jose State", abbrev: "SJSU", conf: "MW" },
          { name: "UNLV", abbrev: "UNLV", conf: "MW" },
          // West Coast
          { name: "Gonzaga", abbrev: "GONZ", conf: "WCC" },
          { name: "Pepperdine", abbrev: "PEPP", conf: "WCC" },
          { name: "San Diego", abbrev: "USD", conf: "WCC" },
          { name: "San Francisco", abbrev: "USF", conf: "WCC" },
          { name: "Santa Clara", abbrev: "SCU", conf: "WCC" },
          // Big East
          { name: "Butler", abbrev: "BUT", conf: "Big East" },
          { name: "Connecticut", abbrev: "CONN", conf: "Big East" },
          { name: "Creighton", abbrev: "CREI", conf: "Big East" },
          { name: "Georgetown", abbrev: "GTWN", conf: "Big East" },
          { name: "St. John's", abbrev: "SJU", conf: "Big East" },
          { name: "Seton Hall", abbrev: "HALL", conf: "Big East" },
          { name: "Villanova", abbrev: "NOVA", conf: "Big East" },
          { name: "Xavier", abbrev: "XAV", conf: "Big East" },
          // A-10
          { name: "Dayton", abbrev: "DAY", conf: "A-10" },
          { name: "Davidson", abbrev: "DAV", conf: "A-10" },
          { name: "Fordham", abbrev: "FOR", conf: "A-10" },
          { name: "George Mason", abbrev: "GMU", conf: "A-10" },
          { name: "George Washington", abbrev: "GWU", conf: "A-10" },
          { name: "La Salle", abbrev: "LAS", conf: "A-10" },
          { name: "Massachusetts", abbrev: "UMASS", conf: "A-10" },
          { name: "Rhode Island", abbrev: "URI", conf: "A-10" },
          { name: "Richmond", abbrev: "RICH", conf: "A-10" },
          { name: "Saint Louis", abbrev: "SLU", conf: "A-10" },
          { name: "St. Bonaventure", abbrev: "BONN", conf: "A-10" },
          { name: "VCU", abbrev: "VCU", conf: "A-10" },
          // Ivy League
          { name: "Brown", abbrev: "BRWN", conf: "Ivy" },
          { name: "Columbia", abbrev: "CLMB", conf: "Ivy" },
          { name: "Cornell", abbrev: "COR", conf: "Ivy" },
          { name: "Dartmouth", abbrev: "DART", conf: "Ivy" },
          { name: "Harvard", abbrev: "HARV", conf: "Ivy" },
          { name: "Penn", abbrev: "PENN", conf: "Ivy" },
          { name: "Princeton", abbrev: "PRIN", conf: "Ivy" },
          { name: "Yale", abbrev: "YALE", conf: "Ivy" },
          // Colonial
          { name: "Campbell", abbrev: "CAM", conf: "CAA" },
          { name: "Delaware", abbrev: "DEL", conf: "CAA" },
          { name: "Drexel", abbrev: "DREX", conf: "CAA" },
          { name: "Elon", abbrev: "ELON", conf: "CAA" },
          { name: "Hofstra", abbrev: "HOF", conf: "CAA" },
          { name: "Monmouth", abbrev: "MON", conf: "CAA" },
          { name: "Northeastern", abbrev: "NEU", conf: "CAA" },
          { name: "Stony Brook", abbrev: "SBU", conf: "CAA" },
          { name: "Towson", abbrev: "TOW", conf: "CAA" },
          { name: "UNC Wilmington", abbrev: "UNCW", conf: "CAA" },
          { name: "William & Mary", abbrev: "WM", conf: "CAA" },
          // Missouri Valley
          { name: "Dallas Baptist", abbrev: "DBU", conf: "MVC" },
          { name: "Evansville", abbrev: "EVAN", conf: "MVC" },
          { name: "Illinois State", abbrev: "ILST", conf: "MVC" },
          { name: "Indiana State", abbrev: "INST", conf: "MVC" },
          { name: "Missouri State", abbrev: "MOST", conf: "MVC" },
          { name: "Southern Illinois", abbrev: "SIU", conf: "MVC" },
          { name: "Valparaiso", abbrev: "VALP", conf: "MVC" },
          // Southern
          { name: "Chattanooga", abbrev: "UTC", conf: "SoCon" },
          { name: "ETSU", abbrev: "ETSU", conf: "SoCon" },
          { name: "Furman", abbrev: "FUR", conf: "SoCon" },
          { name: "Mercer", abbrev: "MER", conf: "SoCon" },
          { name: "Samford", abbrev: "SAM", conf: "SoCon" },
          { name: "The Citadel", abbrev: "CIT", conf: "SoCon" },
          { name: "UNC Greensboro", abbrev: "UNCG", conf: "SoCon" },
          { name: "VMI", abbrev: "VMI", conf: "SoCon" },
          { name: "Western Carolina", abbrev: "WCU", conf: "SoCon" },
          { name: "Wofford", abbrev: "WOF", conf: "SoCon" },
          // Big South / OVC
          { name: "Bellarmine", abbrev: "BELL", conf: "ASUN" },
          { name: "Central Arkansas", abbrev: "UCA", conf: "ASUN" },
          { name: "Eastern Kentucky", abbrev: "EKU", conf: "ASUN" },
          { name: "Florida Gulf Coast", abbrev: "FGCU", conf: "ASUN" },
          { name: "Jacksonville", abbrev: "JU", conf: "ASUN" },
          { name: "Lipscomb", abbrev: "LIP", conf: "ASUN" },
          { name: "North Alabama", abbrev: "UNA", conf: "ASUN" },
          { name: "Queens", abbrev: "QUNS", conf: "ASUN" },
          { name: "Stetson", abbrev: "STET", conf: "ASUN" },
          // America East
          { name: "Albany", abbrev: "ALB", conf: "AE" },
          { name: "Binghamton", abbrev: "BING", conf: "AE" },
          { name: "Bryant", abbrev: "BRY", conf: "AE" },
          { name: "Maine", abbrev: "ME", conf: "AE" },
          { name: "NJIT", abbrev: "NJIT", conf: "AE" },
          { name: "UMBC", abbrev: "UMBC", conf: "AE" },
          { name: "UMass Lowell", abbrev: "UML", conf: "AE" },
          { name: "Vermont", abbrev: "VT", conf: "AE" },
          // Patriot League
          { name: "Army", abbrev: "ARMY", conf: "Patriot" },
          { name: "Bucknell", abbrev: "BUCK", conf: "Patriot" },
          { name: "Holy Cross", abbrev: "HC", conf: "Patriot" },
          { name: "Lafayette", abbrev: "LAF", conf: "Patriot" },
          { name: "Lehigh", abbrev: "LEH", conf: "Patriot" },
          // WAC
          { name: "Abilene Christian", abbrev: "ACU", conf: "WAC" },
          { name: "California Baptist", abbrev: "CBU", conf: "WAC" },
          { name: "Grand Canyon", abbrev: "GCU", conf: "WAC" },
          { name: "Seattle", abbrev: "SEA", conf: "WAC" },
          { name: "Stephen F. Austin", abbrev: "SFA", conf: "WAC" },
          { name: "Tarleton State", abbrev: "TAR", conf: "WAC" },
          { name: "UT Arlington", abbrev: "UTA", conf: "WAC" },
          { name: "Utah Valley", abbrev: "UVU", conf: "WAC" },
          // Big West
          { name: "Cal Poly", abbrev: "CP", conf: "Big West" },
          { name: "Cal State Bakersfield", abbrev: "CSUB", conf: "Big West" },
          { name: "Cal State Fullerton", abbrev: "CSUF", conf: "Big West" },
          { name: "Cal State Northridge", abbrev: "CSUN", conf: "Big West" },
          { name: "Hawaii", abbrev: "HAW", conf: "Big West" },
          { name: "Long Beach State", abbrev: "LBSU", conf: "Big West" },
          { name: "UC Davis", abbrev: "UCD", conf: "Big West" },
          { name: "UC Irvine", abbrev: "UCI", conf: "Big West" },
          { name: "UC Riverside", abbrev: "UCR", conf: "Big West" },
          { name: "UC San Diego", abbrev: "UCSD", conf: "Big West" },
          { name: "UC Santa Barbara", abbrev: "UCSB", conf: "Big West" },
          // Southland
          { name: "Houston Christian", abbrev: "HCU", conf: "Southland" },
          { name: "Incarnate Word", abbrev: "UIW", conf: "Southland" },
          { name: "Lamar", abbrev: "LAM", conf: "Southland" },
          { name: "McNeese", abbrev: "MCN", conf: "Southland" },
          { name: "New Orleans", abbrev: "UNO", conf: "Southland" },
          { name: "Nicholls", abbrev: "NICH", conf: "Southland" },
          { name: "Northwestern State", abbrev: "NSU", conf: "Southland" },
          { name: "Southeastern Louisiana", abbrev: "SELA", conf: "Southland" },
          { name: "Texas A&M-Corpus Christi", abbrev: "TAMUCC", conf: "Southland" },
          // SWAC
          { name: "Alabama A&M", abbrev: "AAMU", conf: "SWAC" },
          { name: "Alabama State", abbrev: "ALST", conf: "SWAC" },
          { name: "Alcorn State", abbrev: "ALCN", conf: "SWAC" },
          { name: "Arkansas-Pine Bluff", abbrev: "UAPB", conf: "SWAC" },
          { name: "Bethune-Cookman", abbrev: "BCU", conf: "SWAC" },
          { name: "Florida A&M", abbrev: "FAMU", conf: "SWAC" },
          { name: "Grambling State", abbrev: "GRAM", conf: "SWAC" },
          { name: "Jackson State", abbrev: "JKST", conf: "SWAC" },
          { name: "Mississippi Valley State", abbrev: "MVSU", conf: "SWAC" },
          { name: "Prairie View A&M", abbrev: "PVAM", conf: "SWAC" },
          { name: "Southern", abbrev: "SOU", conf: "SWAC" },
          { name: "Texas Southern", abbrev: "TXSO", conf: "SWAC" },
          // MEAC
          { name: "Coppin State", abbrev: "COPP", conf: "MEAC" },
          { name: "Delaware State", abbrev: "DSU", conf: "MEAC" },
          { name: "Howard", abbrev: "HOW", conf: "MEAC" },
          { name: "Maryland Eastern Shore", abbrev: "UMES", conf: "MEAC" },
          { name: "Morgan State", abbrev: "MORG", conf: "MEAC" },
          { name: "Norfolk State", abbrev: "NSU", conf: "MEAC" },
          { name: "North Carolina A&T", abbrev: "NCAT", conf: "MEAC" },
          { name: "North Carolina Central", abbrev: "NCCU", conf: "MEAC" },
          { name: "South Carolina State", abbrev: "SCST", conf: "MEAC" },
          // NEC
          { name: "Central Connecticut", abbrev: "CCSU", conf: "NEC" },
          { name: "Fairleigh Dickinson", abbrev: "FDU", conf: "NEC" },
          { name: "LIU", abbrev: "LIU", conf: "NEC" },
          { name: "Merrimack", abbrev: "MERR", conf: "NEC" },
          { name: "Mount St. Mary's", abbrev: "MSM", conf: "NEC" },
          { name: "Sacred Heart", abbrev: "SHU", conf: "NEC" },
          { name: "St. Francis (PA)", abbrev: "SFU", conf: "NEC" },
          { name: "Wagner", abbrev: "WAG", conf: "NEC" },
          // Horizon League
          { name: "Cleveland State", abbrev: "CSU", conf: "Horizon" },
          { name: "Illinois-Chicago", abbrev: "UIC", conf: "Horizon" },
          { name: "Milwaukee", abbrev: "MIL", conf: "Horizon" },
          { name: "Northern Kentucky", abbrev: "NKU", conf: "Horizon" },
          { name: "Oakland", abbrev: "OAK", conf: "Horizon" },
          { name: "Purdue Fort Wayne", abbrev: "PFW", conf: "Horizon" },
          { name: "Wright State", abbrev: "WRST", conf: "Horizon" },
          { name: "Youngstown State", abbrev: "YSU", conf: "Horizon" },
          // Summit League
          { name: "North Dakota State", abbrev: "NDSU", conf: "Summit" },
          { name: "Omaha", abbrev: "OMA", conf: "Summit" },
          { name: "Oral Roberts", abbrev: "ORU", conf: "Summit" },
          { name: "South Dakota State", abbrev: "SDSU", conf: "Summit" },
          { name: "St. Thomas", abbrev: "STU", conf: "Summit" },
          { name: "Western Illinois", abbrev: "WIU", conf: "Summit" },
          // Ohio Valley
          { name: "Austin Peay", abbrev: "APSU", conf: "OVC" },
          { name: "Lindenwood", abbrev: "LIND", conf: "OVC" },
          { name: "Morehead State", abbrev: "MORE", conf: "OVC" },
          { name: "Murray State", abbrev: "MUR", conf: "OVC" },
          { name: "Southeast Missouri", abbrev: "SEMO", conf: "OVC" },
          { name: "SIU Edwardsville", abbrev: "SIUE", conf: "OVC" },
          { name: "Southern Indiana", abbrev: "USI", conf: "OVC" },
          { name: "Tennessee State", abbrev: "TSU", conf: "OVC" },
          { name: "Tennessee Tech", abbrev: "TTU", conf: "OVC" },
          { name: "UT Martin", abbrev: "UTM", conf: "OVC" },
        ];
        enhancedTeams = cbbTeams.map(t => ({ team_id: t.abbrev.toLowerCase(), team_name: t.name, full_name: t.name, conference: t.conf }));
      } else if (enhancedTeams.length === 0 && sport.code === "NBA") {
        // Fallback to hardcoded NBA teams if NatStat didn't return data
        enhancedTeams = NBA_TEAMS.map(t => ({ team_id: t.id.toLowerCase(), team_name: t.name, full_name: t.name, conference: t.conference }));
      } else if (enhancedTeams.length === 0 && sport.code === "WNBA") {
        // Fallback to hardcoded WNBA teams if NatStat didn't return data
        enhancedTeams = WNBA_TEAMS.map(t => ({ team_id: t.id.toLowerCase(), team_name: t.name, full_name: t.name, conference: t.conference }));
      } else if (enhancedTeams.length === 0 && sport.code === "EURO") {
        enhancedTeams = EUROLEAGUE_TEAMS.map(t => ({ team_id: t.id.toLowerCase(), team_name: t.name, full_name: t.name, conference: t.country }));
      } else if (enhancedTeams.length === 0) {
        const userStr = sessionStorage.getItem("user");
        if (userStr) {
          const userData = JSON.parse(userStr);
          if (userData?.id) {
            const response = await axios.get(`${NODE_BACKEND_URL}/api/user/${userData.id}/sports/${sport.code}/teams`, {
              headers: { Authorization: getAuthToken() }
            });
            if (response.data?.success && Array.isArray(response.data.teams)) {
              enhancedTeams = response.data.teams.map(t => ({
                team_id: String(t.TeamID), team_name: t.TeamName || t.Name || "Unknown",
                full_name: t.FullName || t.TeamName || "", conference: t.Conference || ""
              }));
            }
          }
        }
      }

      enhancedTeams.sort((a, b) => a.team_name.localeCompare(b.team_name));
      setTeams(enhancedTeams);

      const savedTeam = sessionStorage.getItem("selectedTeam");
      if (savedTeam) {
        const parsed = JSON.parse(savedTeam);
        if (enhancedTeams.some(t => t.team_id === parsed?.team_id)) {
          setSelectedTeam(parsed);
        }
      }
    } catch (error) {
      if (!error?.response && error?.code === "ERR_NETWORK") goToLogin();
      console.error("Error fetching teams:", error);
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleSetDefaultSport = (sport) => {
    if (defaultSport?.code === sport.code) {
      setDefaultSport(null);
      sessionStorage.removeItem("selectedSport");
    } else {
      setSelectedTeam(null);
      sessionStorage.removeItem("selectedTeam"); // Clear team when sport changes
      setDefaultSport(sport);
      setSelectedSport(sport);
      // Immediately update sessionStorage so other components see the new sport
      sessionStorage.setItem("selectedSport", JSON.stringify(sport));
      if (!selectedSports.some(s => s.code === sport.code)) {
        const updatedSports = [...selectedSports, sport];
        setSelectedSports(updatedSports);
        sessionStorage.setItem("selectedSports", JSON.stringify(updatedSports));
      }
      fetchTeams(sport);
    }
  };

  const handleSportMultiSelect = (sport) => {
    const isSelected = selectedSports.some(s => s.code === sport.code);
    if (isSelected && defaultSport?.code === sport.code) {
      errorMessage("Cannot remove default sport. Change the default first.");
      return;
    }
    const updatedSports = isSelected
      ? selectedSports.filter(s => s.code !== sport.code)
      : [...selectedSports, sport];
    setSelectedSports(updatedSports);

    if (updatedSports.length > 0 && !selectedSport?.code) {
      setSelectedSport(updatedSports[0]);
      fetchTeams(updatedSports[0]);
    } else if (updatedSports.length === 0) {
      setSelectedSport({});
      setTeams([]);
    }
  };

  const handleSave = async () => {
    if (selectedSports.length === 0) return errorMessage("Please select at least one sport!");
    if (selectedSport && !selectedTeam?.team_id) return errorMessage("Please select a team!");
    if (!defaultSport) return errorMessage("Please set a default sport using the star!");

    setIsSaving(true);
    try {
      sessionStorage.setItem("selectedSports", JSON.stringify(selectedSports));
      sessionStorage.setItem("selectedSport", JSON.stringify(defaultSport));
      setSelectedSport(defaultSport);
      if (selectedTeam) sessionStorage.setItem("selectedTeam", JSON.stringify(selectedTeam));

      // Determine the TeamProvider team ID based on the default sport
      // This maps sport codes to their corresponding team configurations
      const getTeamIdForSport = (code) => {
        const matching = getTeamsBySportCode(code);
        if (matching.length > 0) return matching[0];
        const fallback = { 'CBB': 'slam', 'MLB': 'orioles', 'MBB': 'slamball', 'WBB': 'slamball', 'NBA': 'slamball', 'WNBA': 'slamball', 'EURO': 'realMadrid' };
        return fallback[code] || 'slam';
      };

      const teamIdForProvider = getTeamIdForSport(defaultSport?.code);

      // Switch the team in TeamProvider - this automatically derives and sets the sport
      // based on the team's sportCode, updating all platform theming and prompts
      await switchTeam(teamIdForProvider);

      // Derive sport category from the sport code for the event
      const basketballSports = ['NBA', 'MBB', 'WBB', 'WNBA', 'GL', 'EURO'];
      const footballSports = ['NFL', 'CFB'];
      const sportCategory = basketballSports.includes(defaultSport?.code) ? 'basketball'
        : footballSports.includes(defaultSport?.code) ? 'football' : 'baseball';

      // Dispatch event to notify other components about the settings change
      window.dispatchEvent(new CustomEvent('slamSettingsChanged', {
        detail: { sport: defaultSport, team: selectedTeam, teamId: teamIdForProvider, sportCategory }
      }));

      setting.performance_criteria = performanceCriteria;
      sessionStorage.setItem('setting', JSON.stringify(setting));

      await axios.post(`${NODE_BACKEND_URL}/api/update-sport-team`, {
        id: user?.id, sport_type: defaultSport?.code, all_sports: selectedSports.map(s => s.code),
        team_type: selectedTeam?.team_id, performance_criteria: performanceCriteria,
      }, { headers: { Authorization: getAuthToken() } });

      setShowSaveNotification(true);
      setShowLogo(true);
      setTimeout(() => setShowSaveNotification(false), 3000);
      getUserSettings(user?.id);
    } catch (error) {
      console.error("Error saving settings:", error);
      errorMessage("Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const filteredSports = useMemo(() => {
    return sports
      .filter(s => !sportSearch || s.code?.toLowerCase().includes(sportSearch.toLowerCase()) || s.name?.toLowerCase().includes(sportSearch.toLowerCase()))
      .sort((a, b) => (b.isEnable === true) - (a.isEnable === true));
  }, [sports, sportSearch]);

  const filteredTeams = useMemo(() => {
    if (!teamSearch) return teams;
    const search = teamSearch.toLowerCase();
    return teams.filter(t => t.team_name?.toLowerCase().includes(search) || t.conference?.toLowerCase().includes(search));
  }, [teams, teamSearch]);

  // Styles — OLED Black & Gold
  const panelClass = cn(
    "rounded-[14px] border p-5 flex flex-col",
    "bg-[#0A0A0A] border-[#1A1A1A]"
  );
  const inputClass = cn(
    "w-full pl-10 pr-4 py-2 rounded-lg border text-sm outline-none transition-colors",
    "bg-[#111111] border-[#1A1A1A] text-[#F5F5F5] placeholder:text-[#555555] focus:border-[rgba(232,184,0,0.4)]"
  );
  const listItemClass = (isSelected) => cn(
    "flex items-center justify-between px-4 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors",
    "border-[#1A1A1A]",
    isSelected ? "bg-[rgba(232,184,0,0.15)]" : "hover:bg-[rgba(255,255,255,0.04)]"
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-black text-[#F5F5F5]">

      {/* Header */}
      <header className="flex-shrink-0 border-b px-6 py-3 bg-[#0A0A0A] border-[#1A1A1A]">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-[#F5F5F5]">{SETTINGS_LABELS.PAGE_TITLE}</h1>
            <p className="text-xs text-[#555555]">{SETTINGS_LABELS.PAGE_DESCRIPTION}</p>
          </div>
          <div className="flex items-center gap-2">
            {showSaveNotification && (
              <div className="flex items-center gap-2 text-green-500 text-sm">
                <CheckCircle className="h-4 w-4" /> Saved!
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => user?.id && getUserSettings(user?.id)}>Reset</Button>
            <Button onClick={handleSave} disabled={isSaving} size="sm">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4 bg-[#111111]">
              <TabsTrigger value="sport">Sport & Team</TabsTrigger>
              <TabsTrigger value="navigation">Navigation</TabsTrigger>
              <TabsTrigger value="athleticDepartment">Athletic Department</TabsTrigger>
            </TabsList>

            {/* Sport & Team Tab */}
            <TabsContent value="sport" className="m-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* Sport Selection */}
                <div className={panelClass}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-[rgba(232,184,0,0.15)]">
                      <Users className="h-4 w-4 text-[#E8B800]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm text-[#F5F5F5]">Default Sport</h3>
                      <p className="text-xs text-[#555555]">Set your default sport to filter teams</p>
                    </div>
                  </div>

                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#555555]" />
                    <input type="text" placeholder="Search..." value={sportSearch} onChange={(e) => setSportSearch(e.target.value)} className={inputClass} />
                  </div>

                  {loadingSports ? (
                    <div className="flex-1 flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-[#555555]" />
                    </div>
                  ) : (
                    <div className="rounded-[14px] border overflow-hidden border-[#1A1A1A]">
                      <div className="max-h-[280px] overflow-y-auto">
                        {filteredSports.map((sport) => {
                          const isSelected = selectedSports.some(s => s.code === sport.code);
                          const isDefault = defaultSport?.code === sport.code;
                          return (
                            <div
                              key={sport.code}
                              onClick={() => sport?.isEnable && handleSportMultiSelect(sport)}
                              className={cn(listItemClass(isSelected), !sport?.isEnable && "opacity-40 cursor-not-allowed")}
                            >
                              <div className="flex items-center gap-2">
                                <input type="checkbox" checked={isSelected} disabled={!sport?.isEnable} readOnly className="h-3.5 w-3.5" />
                                <span className="font-medium text-sm text-[#F5F5F5]">{sport.code}</span>
                                {sport.name !== sport.code && <span className="text-xs text-[#555555]">{sport.name}</span>}
                              </div>
                              {sport?.isEnable && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSetDefaultSport(sport); }}
                                  className={cn("p-1 rounded transition-colors", isDefault ? "text-[#E8B800]" : "text-[#555555] hover:text-[#E8B800]")}
                                >
                                  <Star className={cn("h-4 w-4", isDefault && "fill-current")} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-[#555555]"><strong className="text-[#F5F5F5]">{selectedSports.length}</strong> selected</span>
                    {defaultSport && (
                      <Badge variant="outline" className="text-[#E8B800] border-[rgba(232,184,0,0.3)] text-xs">
                        <Star className="h-3 w-3 mr-1 fill-current" />{defaultSport.code}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Team Selection */}
                <div className={panelClass}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-[rgba(232,184,0,0.15)]">
                      <Target className="h-4 w-4 text-[#E8B800]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm text-[#F5F5F5]">Default Team</h3>
                      <p className="text-xs text-[#555555]">{selectedSport?.name ? `Platform adapts to ${selectedSport.name}` : "Select a sport first"}</p>
                    </div>
                  </div>

                  {loadingTeams ? (
                    <div className="flex-1 flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-[#555555]" />
                    </div>
                  ) : teams.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-8 rounded-[14px] border-2 border-dashed border-[#1A1A1A]">
                      <Target className="h-6 w-6 text-[#555555] mb-2" />
                      <p className="text-xs text-[#555555] text-center">Set a default sport to see available teams.<br/>Your team selection determines platform theming.</p>
                    </div>
                  ) : (
                    <>
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#555555]" />
                        <input type="text" placeholder="Search teams..." value={teamSearch} onChange={(e) => setTeamSearch(e.target.value)} className={inputClass} />
                      </div>

                      <div className="rounded-[14px] border overflow-hidden border-[#1A1A1A]">
                        <div className="max-h-[280px] overflow-y-auto">
                          {filteredTeams.map((team) => {
                            const isSelected = selectedTeam?.team_id === team.team_id;
                            return (
                              <div key={team.team_id} onClick={() => {
                                setSelectedTeam(team);
                                // Immediately update sessionStorage so other components see the new team
                                sessionStorage.setItem("selectedTeam", JSON.stringify(team));
                                // Notify other components about the team change
                                notifyTeamChange();
                              }} className={listItemClass(isSelected)}>
                                <div>
                                  <p className={cn("font-medium text-sm text-[#F5F5F5]", isSelected && "text-[#E8B800]")}>{team.team_name}</p>
                                  {team.conference && <p className="text-xs text-[#555555]">{team.conference}</p>}
                                </div>
                                {isSelected && <CheckCircle className="h-4 w-4 text-[#E8B800]" />}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {selectedTeam && (
                        <div className="mt-3 px-3 py-2 rounded-lg text-xs bg-[rgba(232,184,0,0.15)] border border-[rgba(232,184,0,0.3)]">
                          <span className="text-[#999999]">Selected: </span>
                          <span className="font-semibold text-[#E8B800]">{selectedTeam.team_name}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Navigation Tab */}
            <TabsContent value="navigation" className="m-0">
              <div className={panelClass}>
                <NavigationSettings isDarkMode={true} />
              </div>
            </TabsContent>

            {/* Athletic Department Tab */}
            <TabsContent value="athleticDepartment" className="m-0">
              <div className={panelClass}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-[rgba(232,184,0,0.15)]">
                    <Target className="h-4 w-4 text-[#E8B800]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-[#F5F5F5]">Performance Criteria</h3>
                    <p className="text-xs text-[#555555]">Define player evaluation requirements</p>
                  </div>
                </div>

                <div className="mb-3 p-3 rounded-lg text-xs bg-[#111111] border border-[#1A1A1A] text-[#999999]">
                  <strong>Note:</strong> Define performance metrics, academic expectations, character assessment, eligibility criteria, and health requirements.
                </div>

                <textarea
                  value={performanceCriteria}
                  onChange={(e) => setPerformanceCriteria(e.target.value)}
                  className="w-full p-3 min-h-[200px] rounded-lg border text-sm resize-none outline-none bg-[#111111] border-[#1A1A1A] text-[#F5F5F5] placeholder:text-[#555555] focus:border-[rgba(232,184,0,0.4)]"
                  placeholder="Enter performance criteria..."
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Settings;
